import SelectableTile from '@carvana/ds-selectable-tile';
import omit from 'lodash.omit';
import { useContext, useState } from 'react';
import {
  CardImage,
  HeadlineRow,
  ImageContainer,
  ImageUnavailable,
  PriceContainer,
  PriceLabel,
  PriceValue,
  StockCount,
  Title
} from '@/new-cars/common/ConfiguratorCard.styles';
import tileStyles from '@/new-cars/common/SelectableTile.module.css';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { ParentModelResult } from '~/contracts/new-cars';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { isFallbackImage } from '~/utilities/isFallbackImage';
import optimizeCardImage from '~/utilities/new-cars/optimizeCardImage';

type OwnProps = {
  disabled?: boolean;
  selected: boolean;
  year: ParentModelResult;
};

export default function YearCard({
  disabled = false,
  selected,
  year
}: OwnProps) {
  const { setSelectedYear } = useContext(ParentModelContext);
  const [isImageBroken, setIsImageBroken] = useState(false);

  const { count, imageUrl, lowestPrice, title } = year;
  const isFallback = isFallbackImage(imageUrl);
  const optimizedImageUrl = optimizeCardImage(imageUrl, isFallback);

  return (
    <SelectableTile
      disabled={disabled}
      selected={selected}
      onClick={() => setSelectedYear(omit({ ...year, ...year.next }, 'next'))}
      data-analytics-attributes={JSON.stringify({
        level: 'Year',
        section: 'Year',
        text: title,
        tileAction: selected ? 'selected' : 'deselected'
      })}
      data-analytics-id="New Cars"
      data-testid="year-card"
      value={title}
      variant="simple-select"
      className={tileStyles.tile}
    >
      <ImageContainer disabled={disabled} isFallback={isFallback}>
        {optimizedImageUrl && !isImageBroken ? (
          <CardImage
            alt={title}
            data-testid="year-card-image"
            onError={() => setIsImageBroken(true)}
            src={optimizedImageUrl}
          />
        ) : (
          <ImageUnavailable>Image unavailable</ImageUnavailable>
        )}
      </ImageContainer>
      <HeadlineRow>
        <Title disabled={disabled}>{title}</Title>
        <PriceContainer data-testid="year-card-price">
          <PriceLabel>from </PriceLabel>
          <PriceValue disabled={disabled}>
            ${formatAndDropDecimal(lowestPrice)}
          </PriceValue>
        </PriceContainer>
      </HeadlineRow>
      <StockCount data-testid="year-card-stock-count">
        {count} in stock
      </StockCount>
    </SelectableTile>
  );
}
